import React, { ReactNode } from 'react'

import { useGetUserProfileQuery } from '../../../../api/combined'
import { LockedFeature } from '../../../../components/LockedFeature/LockedFeature'
import usePage from '../../../../hooks/usePage'
import PageService from '../../../../services/PageService'
import { LockedFeatureId } from '../../../../types/LockedFeature'
import { useLiveEventsTrackerAccessCheck } from '../../../account/hooks/roleHooks'

export interface LiveEventAnalyticsAccessGateProps {
  children: ReactNode
}

/**
 * The gated page never mounts for locked users, so its own `usePage` call never runs; register
 * the Live Event Analytics page here or the header keeps the previous page's title.
 */
const LiveEventAnalyticsLockedPage: React.FC = () => {
  usePage(PageService.getPageWithId('market-analytics'))

  return <LockedFeature.Card lockedFeatureId={LockedFeatureId.LiveEventAnalytics} />
}

/**
 * Role gate for every Live Event Analytics page. The tool ships as part of Live Events Tracker,
 * so users without the `live_events_tracker` role get the Live Event Analytics unlock card
 * instead of the page, and the page's queries never fire for them. The sidebar entry stays
 * visible for everyone so the feature can be discovered.
 */
export const LiveEventAnalyticsAccessGate: React.FC<LiveEventAnalyticsAccessGateProps> = ({ children }) => {
  const { isLoading: isProfileLoading } = useGetUserProfileQuery()
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()

  // Roles come with the profile; deciding earlier would flash the unlock card for entitled users.
  if (isProfileLoading) return null

  return hasLiveEventsTrackerAccess ? <>{children}</> : <LiveEventAnalyticsLockedPage />
}

/** Wraps a page component so it renders behind the Live Event Analytics role gate. */
export const withLiveEventAnalyticsAccess = (Page: React.ComponentType): React.FC => {
  const GatedPage: React.FC = () => (
    <LiveEventAnalyticsAccessGate>
      <Page />
    </LiveEventAnalyticsAccessGate>
  )
  GatedPage.displayName = `withLiveEventAnalyticsAccess(${Page.displayName || Page.name || 'Page'})`
  return GatedPage
}
